import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { isMobileUserAgent } from 'helpers/userAgent';
import { isTvCheckoutVisit } from 'helpers/tvCheckoutVisit';

const STORAGE_KEY = 'millions_open_in_app_dismissed_at';
const COOLDOWN_MS = 60 * 1000;
const SHOW_DELAY_MS = 600;

export const useOpenInAppModal = (options: {
  mobileOnly?: boolean;
  delay?: number;
  when?: boolean;
}) => {
  const { mobileOnly = true, delay = SHOW_DELAY_MS, when = true } = options;
  const { pathname } = useRouter();

  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const dismissStayOnWeb = useCallback(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }
    close();
  }, [close]);

  useEffect(() => {
    if (!when || typeof window === 'undefined') return;
    if (isTvCheckoutVisit(window.location.search)) return;

    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - Number(dismissedAt);
      if (elapsed < COOLDOWN_MS) return;
    }

    const isMobile = mobileOnly && isMobileUserAgent();
    if (!isMobile) return;

    const timer = window.setTimeout(() => {
      setIsOpen(true);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [when, mobileOnly, delay, pathname]);

  return {
    isOpen,
    open,
    close,
    dismissStayOnWeb,
  };
};
